/**
 * Operation Management - Edit Task Modal
 *
 * Full-width dialog matching Figma design 664:62267
 * Sections: Task Details, Schedule, Status
 * RTL layout with Arabic labels
 */

import { useState, useEffect, useContext } from 'react';
import { Box, Typography, Dialog, IconButton, Select, MenuItem, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuX, LuLock, } from 'react-icons/lu';
import { ToastMessageContext } from '@context/toast-message-cotnext';
import { useGetMaintenanceTask } from '../services/operations.queries';
import { useUpdateMaintenanceTask } from '../services/operations.mutations';
import type { IMaintenanceTaskListItem, IMaintenanceTaskDetailData } from '../types';

interface IEditTaskModalProps {
  open: boolean;
  onClose: () => void;
  task: IMaintenanceTaskListItem | null;
  onSuccess: () => void;
}

// --- Section Divider — Figma: line on left side of label (in RTL visual) ---
const SectionDivider = ({ label }: { label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', height: '17px' }}>
    <Typography
      sx={{
        fontFamily: 'Cairo, sans-serif',
        fontWeight: 700,
        fontSize: '11px',
        color: '#9CA3AF',
        letterSpacing: '0.55px',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', backgroundColor: '#F3F4F6' }} />
  </Box>
);

// --- Read-only Field ---
const ReadOnlyField = ({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#8E98A1', textAlign: 'start' }}>
      {label}
    </Typography>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '44px',
        px: '17px',
        borderRadius: '8px',
        backgroundColor: '#F9FAFB',
        border: '1px solid #F3F4F6',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon}
        <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '16px', color: '#6B7280' }}>
          {value || '—'}
        </Typography>
      </Box>
      <LuLock size={12} color="#9CA3AF" />
    </Box>
  </Box>
);

// --- Dropdown Field ---
const DropdownField = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string; color?: string }[];
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#8E98A1', textAlign: 'start' }}>
      {label}
    </Typography>
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        height: '44px',
        borderRadius: '8px',
        fontFamily: 'Tajawal, sans-serif',
        fontSize: '16px',
        fontWeight: 500,
        color: options.find((o) => o.value === value)?.color || '#435363',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9D9E9' },
        '& .MuiSelect-select': { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '16px', color: opt.color || '#435363' }}>
            {opt.label}
          </Typography>
        </MenuItem>
      ))}
    </Select>
  </Box>
);

// Maps detail API status values to the standardized option values used in the dropdown
const normalizeStatus = (s: string): string => {
  const lower = (s || '').toLowerCase();
  if (lower === 'pending' || lower === 'awaiting_processing') return 'awaiting_processing';
  if (lower === 'scheduled' || lower === 'assigned') return 'assigned';
  if (lower === 'inprogress' || lower === 'in_progress' || lower === 'in progress') return 'in_progress';
  if (lower === 'completed' || lower === 'done') return 'completed';
  if (lower === 'canceled' || lower === 'cancelled') return 'canceled';
  return s;
};

const normalizePriority = (p: string): string => {
  const lower = (p || '').toLowerCase();
  if (lower === 'emergency') return 'emergency';
  if (lower === 'urgent' || lower === 'high') return 'urgent';
  if (lower === 'routine' || lower === 'medium' || lower === 'normal') return 'routine';
  if (lower === 'low') return 'low';
  return p;
};

const EditTaskModal = ({ open, onClose, task, onSuccess }: IEditTaskModalProps) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const { showSuccess, showError } = useContext(ToastMessageContext);

  // Fetch detail — response: { result: { data: IMaintenanceTaskDetailData } }
  const { data: detailData, isLoading: detailLoading } = useGetMaintenanceTask(
    task?.id
  );
  const detail: IMaintenanceTaskDetailData | undefined = detailData?.result?.nabeehData ?? detailData?.result?.data;
  const deadlineExpression = isRtl
    ? detailData?.result?.deadlineExpressionAr
    : detailData?.result?.deadlineExpressionEn;
  const serviceProviderPhone = detailData?.result?.serviceProviderPhoneNumber;
  const taskSource = isRtl
    ? (detailData?.result?.sourceAr || detail?.type)
    : (detailData?.result?.sourceEn || detail?.type);
  const taskTimeRaw = detailData?.result?.taskTime;

  // Editable state
  const [taskStatus, setTaskStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [taskDate, setTaskDate] = useState('');
  const [taskTime, setTaskTime] = useState('');

  // Sync all editable state from detail API — normalise status/priority so they match option values.
  // Date/time: prefer detail.scheduled_at (fresh from API) over task.taskDateTime (list row cache).
  useEffect(() => {
    if (detail) {
      setTaskStatus(normalizeStatus(detail.status || ''));
      setPriority(normalizePriority(detail.priority || ''));
      const dateTimeStr = detail.scheduled_at || task?.taskDateTime;
      if (dateTimeStr) {
        const parts = dateTimeStr.split('T');
        setTaskDate(parts[0] || '');
        if (parts[1]) {
          const hm = parts[1].split(':');
          setTaskTime(`${hm[0]}:${hm[1]}`);
        }
      }
    } else if (task) {
      // No detail API (null requestId) or still loading — fall back to list item data
      setTaskStatus(normalizeStatus(task.taskStatus || ''));
      setPriority(normalizePriority(task.taskPriority || ''));
      if (task.taskDateTime) {
        const parts = task.taskDateTime.split('T');
        setTaskDate(parts[0] || '');
        if (parts[1]) {
          const hm = parts[1].split(':');
          setTaskTime(`${hm[0]}:${hm[1]}`);
        }
      }
    }
  }, [detail, task]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTaskStatus('');
      setPriority('');
      setTaskDate('');
      setTaskTime('');
    }
  }, [open]);

  // Update mutation
  const updateMutation = useUpdateMaintenanceTask();

  const handleSave = async () => {
    const requestId = task?.maintenanceRequestId;
    if (!requestId) return;

    // Extract timezone offset from the original taskDateTime (e.g. "+03:00") to preserve it
    let tzOffset = '';
    if (task?.taskDateTime) {
      const match = task.taskDateTime.match(/([+-]\d{2}:\d{2})$/);
      if (match) tzOffset = match[1];
    }
    // Normalise time to HH:MM:SS — guard against browser returning HH:MM:SS already
    const timeParts = (taskTime || '00:00').split(':');
    const normTime = `${timeParts[0]}:${timeParts[1]}:${timeParts[2] || '00'}`;
    const scheduled_at = taskDate
      ? `${taskDate}T${normTime}${tzOffset}`
      : '';

    try {
      const response = await updateMutation.mutateAsync({
        requestId,
        body: { taskDueAt: scheduled_at, assignee: task?.serviceProviderId || '', taskStatus, priority },
      });
      if (response.isSuccess) {
        showSuccess(t('OperationManagement.taskUpdatedSuccess'));
        onSuccess();
        onClose();
      } else {
        const msg = isRtl
          ? response.errorMessages?.[0]?.messageAr
          : response.errorMessages?.[0]?.messageEn;
        showError(msg || t('ERRORS.SOMETHING_WENT_WRONG'));
      }
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
    }
  };

  // Derive original date/time from task.taskDateTime to detect changes
  let origDate = '';
  let origTime = '';
  if (task?.taskDateTime) {
    const parts = task.taskDateTime.split('T');
    origDate = parts[0] || '';
    if (parts[1]) {
      const hm = parts[1].split(':');
      origTime = `${hm[0]}:${hm[1]}`;
    }
  }

  const hasChanges = detail
    ? taskStatus !== (detail.status || '') ||
      priority !== (detail.priority || '') ||
      taskDate !== origDate ||
      taskTime !== origTime
    : false;

  // Reference number from detail API
  const requestReference = detail?.request_reference || '';

  // Priority options — values match real API: emergency, urgent, routine, low
  const priorityOptions = [
    { value: 'emergency', label: t('OperationManagement.priorityEmergency'), color: '#991B1B' },
    { value: 'urgent', label: t('OperationManagement.priorityHigh'), color: '#DC2626' },
    { value: 'routine', label: t('OperationManagement.priorityMedium'), color: '#D97706' },
    { value: 'low', label: t('OperationManagement.priorityLow'), color: '#2563EB' },
  ];

  // Status options — values match API enums: awaiting_processing, assigned, in_progress, completed, canceled
  const statusOptions = [
    { value: 'awaiting_processing', label: t('OperationManagement.statusWaiting') },
    { value: 'assigned', label: t('OperationManagement.statusScheduled') },
    { value: 'in_progress', label: t('OperationManagement.statusInProgress') },
    { value: 'completed', label: t('OperationManagement.statusDone') },
    { value: 'canceled', label: t('OperationManagement.statusCancelled') },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          maxHeight: '90vh',
        },
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', pt: '24px', px: '24px', pb: '16px' }}>
          {/* Header — Figma: X close button at top-left visually (flex-start in RTL) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: '16px' }}>
            <IconButton onClick={onClose} sx={{ color: '#435363' }}>
              <LuX size={24} />
            </IconButton>
          </Box>

          {/* Title — centered per Figma */}
          <Box sx={{ mb: '24px', textAlign: 'start' }}>
            <Typography
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontWeight: 700,
                fontSize: '24px',
                color: '#261B6A',
                letterSpacing: '-0.6px',
              }}
            >
              {t('OperationManagement.editTask')}
            </Typography>
            <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '16px', color: '#666', mt: '4px' }}>
              {t('OperationManagement.editTaskDescription')}
            </Typography>
          </Box>

          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <div
                className="animate-spin rounded-full h-8 w-8"
                style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* --- Section: Task Details --- */}
              <SectionDivider label={t('OperationManagement.sectionTaskDetails')} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ReadOnlyField
                  label={t('OperationManagement.fieldTaskType')}
                  value={isRtl ? (task?.maintenanceTypeAr || task?.maintenanceType || '') : (task?.maintenanceTypeEn || task?.maintenanceType || '')}
                />
                <ReadOnlyField
                  label={t('OperationManagement.fieldProperty')}
                  value={task?.propertyName || ''}
                />

                {/* Assignee — read-only (from list item) */}
                <ReadOnlyField
                  label={t('OperationManagement.fieldAssignee')}
                  value={task?.serviceProviderName || '—'}
                />
                {serviceProviderPhone && (
                  <ReadOnlyField
                    label={t('OperationManagement.fieldAssigneePhone') || 'رقم المسؤول'}
                    value={serviceProviderPhone}
                  />
                )}
              </Box>

              {/* --- Section: Schedule --- */}
              <SectionDivider label={t('OperationManagement.sectionSchedule')} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Date + Time labels row */}
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#8E98A1' }}>
                      {t('OperationManagement.fieldTaskDate')}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#8E98A1' }}>
                      {t('OperationManagement.fieldDueTime')}
                    </Typography>
                  </Box>
                </Box>
                {/* Date + Time inputs row — Figma: date on right (first in RTL), time on left */}
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  {/* Task Date — appears on right in RTL */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'flex-start' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        height: '48px',
                        px: '17px',
                        borderRadius: '8px',
                        border: '1px solid #D9D9E9',
                        backgroundColor: '#FFFFFF',
                      }}
                    >
                      <input
                        type="date"
                        value={taskDate}
                        onChange={(e) => setTaskDate(e.target.value)}
                        style={{
                          border: 'none',
                          outline: 'none',
                          fontFamily: 'Tajawal, sans-serif',
                          fontSize: '16px',
                          color: '#435363',
                          background: 'transparent',
                          width: '100%',
                          textAlign: 'right',
                        }}
                      />
                    </Box>
                  </Box>
                  {/* Due Time — appears on left in RTL */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'flex-start' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        height: '48px',
                        px: '17px',
                        borderRadius: '8px',
                        border: '1px solid #D9D9E9',
                        backgroundColor: '#FFFFFF',
                      }}
                    >
                      <input
                        type="time"
                        value={taskTime}
                        onChange={(e) => setTaskTime(e.target.value)}
                        style={{
                          border: 'none',
                          outline: 'none',
                          fontFamily: 'Tajawal, sans-serif',
                          fontSize: '16px',
                          color: '#435363',
                          background: 'transparent',
                          width: '100%',
                          textAlign: 'right',
                        }}
                      />
                    </Box>
                  </Box>

                </Box>

                {/* Deadline warning banner — from deadlineExpressionAr/En */}
                {deadlineExpression && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      backgroundColor: '#FFFBEB',
                      border: '1px solid #FDE68A',
                      borderRadius: '8px',
                      p: '12px',
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#92400E', lineHeight: 1.5 }}>
                      ⚠️ {deadlineExpression}
                    </Typography>
                  </Box>
                )}
                {/* Task due time from nabeeh (taskTime) — read-only */}
                {taskTimeRaw && (() => {
                  const parts = taskTimeRaw.split('T');
                  const dueDateStr = parts[0] || '';
                  const dueTimeStr = parts[1] ? parts[1].substring(0, 5) : '';
                  const display = [dueDateStr, dueTimeStr].filter(Boolean).join(' — ');
                  return (
                    <ReadOnlyField
                      label={t('OperationManagement.fieldDueTime')}
                      value={display}
                    />
                  );
                })()}
              </Box>

              {/* --- Section: Status --- */}
              <SectionDivider label={t('OperationManagement.sectionStatus')} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Status + Priority labels */}
                <Box sx={{ display: 'flex', gap: '12px' }}>
                  <Box sx={{ flex: 1, textAlign: 'start' }}>
                    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#8E98A1' }}>
                      {t('OperationManagement.fieldPriority')}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'start' }}>
                    <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#8E98A1' }}>
                      {t('OperationManagement.fieldTaskStatus')}
                    </Typography>
                  </Box>
                </Box>
                {/* Status + Priority dropdowns — Figma: taskStatus on right, priority on left in RTL */}
                <Box sx={{ display: 'flex', gap: '12px' }}>
                  <Box sx={{ flex: 1 }}>
                    <Select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      fullWidth
                      sx={{
                        height: '48px',
                        borderRadius: '8px',
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '16px',
                        fontWeight: 700,
                        color: priorityOptions.find((o) => o.value === priority)?.color || '#435363',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9D9E9' },
                      }}
                    >
                      {priorityOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '16px', fontWeight: 700, color: opt.color }}>
                            {opt.label}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Select
                      value={taskStatus}
                      onChange={(e) => setTaskStatus(e.target.value)}
                      fullWidth
                      sx={{
                        height: '48px',
                        borderRadius: '8px',
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '16px',
                        fontWeight: 700,
                        color: '#435363',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9D9E9' },
                      }}
                    >
                      {statusOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '16px', fontWeight: 700 }}>
                            {opt.label}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>

                {/* Task Source — read-only (sourceAr/En from new API, falls back to type) */}
                <ReadOnlyField
                  label={t('OperationManagement.fieldTaskSource')}
                  value={taskSource || task?.serviceId || '—'}
                />

                {/* Linked Reservation — read-only */}
                <ReadOnlyField
                  label={t('OperationManagement.fieldLinkedReservation')}
                  value={task?.reservationId ? `#${task.reservationId}` : '—'}
                />

                {/* Reference number — read-only */}
                {requestReference && (
                  <ReadOnlyField
                    label={t('OperationManagement.fieldReference') || 'Reference'}
                    value={requestReference}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer — Figma: cancel (purple outlined) on right in RTL = first in DOM, save (disabled) on left */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            px: '24px',
            pb: '24px',
            pt: '25px',
            borderTop: '1px solid #E8E8EA',
            borderRadius: '0 0 24px 24px',
            backgroundColor: '#FFFFFF',
          }}
        >
          {/* Cancel button — Figma: purple outlined, 200px, appears on right in RTL */}
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              width: '200px',
              height: '48px',
              borderRadius: '8px',
              borderColor: '#5B3FFF',
              color: '#5B3FFF',
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 700,
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': { borderColor: '#4930CC', backgroundColor: '#F5F3FF' },
            }}
          >
            {t('OperationManagement.cancel')}
          </Button>

          {/* Save button — Figma: grey disabled by default, 200px, appears on left in RTL */}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            sx={{
              width: '200px',
              height: '48px',
              borderRadius: '8px',
              backgroundColor: hasChanges ? '#5B3FFF' : '#DDD',
              color: hasChanges ? '#FFFFFF' : '#BDBDBD',
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 700,
              fontSize: '14px',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { backgroundColor: hasChanges ? '#4930CC' : '#DDD', boxShadow: 'none' },
              '&.Mui-disabled': { backgroundColor: '#DDD', color: '#BDBDBD' },
            }}
          >
            {updateMutation.isPending ? '...' : t('OperationManagement.saveChanges')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditTaskModal;
